import { AnimatePresence, motion, useInView } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { ContactCTA } from "../ContactCTA";
import { SERVICES, SERVICES_NOTE } from "../../data/agencyData";

/**
 * The scattered service pills from build/OurServicesSection.svg.
 *
 * The design has them loosely in two columns, each at its own angle and height —
 * so this is a two-column grid where every pill carries its own tilt and offset,
 * alternating sides as it flows. Below `md` the pills shrink and the scatter is
 * damped (see .gc-pill-item) so they still wrap two and three to a row instead
 * of each claiming one — the asymmetry survives, the single column does not.
 *
 * Pill fill is the page background itself; the raised look comes entirely from
 * the shadow, exactly as drawn.
 *
 * Clicking a pill opens a white card beneath it with a one-line description
 * (no prices — see agencyData). The card sits inside the pill's own <li>, in
 * normal flow, so opening it grows that item and every other pill glides to make
 * room — the movement is `layout` on each <li>, not a scripted scatter. The open
 * pill stops drifting and straightens so the card hangs squarely below it.
 */
export function ServicesSection() {
  const ref = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // Escape, or a click anywhere outside the pills, closes the open card.
  useEffect(() => {
    if (openIndex === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenIndex(null);
    }
    function onPointerDown(e: PointerEvent) {
      if (!listRef.current?.contains(e.target as Node)) setOpenIndex(null);
    }
    window.addEventListener("keydown", onKey);
    // Capture phase, so this runs before the pill's own click can reopen it.
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [openIndex]);

  return (
    <section ref={ref} className="relative w-full py-16 md:py-24 px-4 flex flex-col items-center gap-8">
      <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">What we do</p>
      <h2 className="font-black text-[48px] md:text-[96px] leading-[1.05] tracking-[-2.4px] text-[#141414] text-center">
        Our Services
      </h2>

      <p className="text-[15px] md:text-[17px] leading-[1.6] text-[#666] text-center max-w-[720px]">
        {SERVICES_NOTE}
      </p>

      {/*
        A wrapping flow, not a grid. Because the labels differ so much in width,
        rows fall out at two to four pills and never line up — which is the
        asymmetry the design has. A fixed column count cannot produce it, and
        absolute positioning would collide as soon as a label changed length.
      */}
      {/* gap-y clears the vertical overhang of a tilted pill; gap-x is wider than
          any nudge so neighbours can never be pulled into each other. */}
      <ul ref={listRef} className="w-full max-w-[1120px] mt-4 flex flex-wrap items-start justify-center gap-x-2.5 gap-y-3 md:gap-x-6 md:gap-y-6">
        {SERVICES.map((service, i) => {
          const open = openIndex === i;
          const dimmed = openIndex !== null && !open;
          const panelId = `service-desc-${i}`;
          return (
            <motion.li
              key={service.label}
              layout
              initial={{ opacity: 0, y: 18 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
              transition={{ duration: 0.45, delay: (i % 8) * 0.05, ease: "easeOut" }}
              // The scatter offsets live in CSS (see .gc-pill-item) rather than
              // inline, so a phone can scale them down: at full strength a 44px
              // nudge is an eighth of the viewport, and every pill was being
              // pushed wide enough to claim a row of its own.
              className="gc-pill-item max-w-full"
              style={
                {
                  "--gc-pill-offset": `${service.offset}px`,
                  "--gc-pill-nudge": `${service.nudge}px`,
                } as React.CSSProperties
              }
            >
              {/* Drift lives on its own element — see .gc-float in globals.css.
                  Periods are prime-ish multiples so the pills never sync up.
                  The open pill drops out of the float and stacks its card below. */}
              <span
                className={open ? "flex flex-col items-center" : "gc-float inline-block"}
                style={
                  {
                    "--gc-float-dur": `${5 + (i % 5) * 0.7}s`,
                    "--gc-float-delay": `${(i % 7) * 0.45}s`,
                  } as React.CSSProperties
                }
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? null : i)}
                  aria-expanded={open}
                  aria-controls={open ? panelId : undefined}
                  style={{ rotate: open ? "0deg" : `${service.tilt}deg`, color: service.color }}
                  className={`inline-block rounded-full px-4 py-2.5 md:px-8 md:py-4 shadow-[0px_8px_16px_-6px_rgba(0,62,91,0.32)] md:shadow-[0px_12px_22px_-8px_rgba(0,62,91,0.28)] transition-all duration-300 hover:scale-[1.05] hover:rotate-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01E12D] focus-visible:ring-offset-2 focus-visible:ring-offset-[#e7f1f1] ${
                    open ? "bg-white ring-2 ring-[#01E12D] scale-[1.05]" : "bg-[#e7f1f1]"
                  } ${dimmed ? "opacity-45" : "opacity-100"}`}
                >
                  <span className="font-semibold text-[13px] md:text-[24px] leading-[1.25] whitespace-nowrap">
                    {service.label}
                  </span>
                </button>

                <AnimatePresence>
                  {open && (
                    <motion.div
                      id={panelId}
                      role="region"
                      aria-label={`${service.label} — what this is`}
                      initial={{ opacity: 0, y: -8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.96 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="mt-3 w-[280px] max-w-[82vw] rounded-2xl bg-white p-5 text-left shadow-[0px_18px_40px_-12px_rgba(0,62,91,0.3)]"
                    >
                      <p className="text-[15px] leading-[1.55] text-[#141414]">{service.desc}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </span>
            </motion.li>
          );
        })}
      </ul>

      <div className="w-full max-w-[1000px] mt-10">
        <ContactCTA message="Let's work together" buttonText="Contact us" scrollToId="contact" />
      </div>
    </section>
  );
}
